import type { TFunction } from 'i18next';

import { ApiHttpError } from '@/types';

/**
 * 封面上傳錯誤 → 文案(R245;BE docs/92 §3)
 * -----------------------------------------------------------------------------
 * 放在 `features/covers/` 而不是 `features/projects/`:同一組錯誤碼由
 * `POST /projects/{pid}/cover` 與 `POST /brand_kits/{bid}/cover` 共用,兩邊各寫一份
 * 必然漂移。i18n 也因此掛在 `common` namespace 而不是任何單一功能的 namespace。
 *
 * **BE 的錯誤 shape**:`HTTPException(status_code=413, detail=ResponseError.COVER_*)`
 * 而 `ResponseError.COVER_*` 是 `Error(code=..., message=...)` 物件 → wire 上是
 * `{"detail":{"code":"COVER_SIZE_EXCEEDS_LIMIT","message":"..."}}`。`httpRequest` 的
 * 非 2xx 分支有 `extractDetailObjectError` fallback,所以 `err.error.code` 讀得到
 * ——**不需要為了 cover 改 transport 的錯誤解析**(已逐行取證)。
 *
 * ⚠️ **任何錯誤都不會改到資料**(BE docs/92 §3):封面沒有 GCS 暫存也沒有兩段式
 * 狀態,失敗就是失敗、原封面原封不動(後端測試直接斷言寫入函式零呼叫)。所以
 * 呼叫端只要顯示訊息 + 保留畫面上的舊封面 —— **不要寫回滾、不要寫重試補償、
 * 不要清理任何東西**。
 */
export type CommonTranslate = TFunction<'common'>;

export type CoverErrorClass =
  | 'COVER_SIZE_EXCEEDS_LIMIT'
  | 'COVER_MIME_NOT_ALLOWED'
  | 'COVER_DIMENSIONS_EXCEED_LIMIT'
  | 'COVER_DECODE_FAILED'
  | 'COVER_COMPRESS_FAILED';

export const COVER_ERROR_CLASSES: ReadonlyArray<CoverErrorClass> = [
  'COVER_SIZE_EXCEEDS_LIMIT',
  'COVER_MIME_NOT_ALLOWED',
  'COVER_DIMENSIONS_EXCEED_LIMIT',
  'COVER_DECODE_FAILED',
  'COVER_COMPRESS_FAILED',
];

export function isKnownCoverErrorClass(code: unknown): code is CoverErrorClass {
  return typeof code === 'string' && (COVER_ERROR_CLASSES as ReadonlyArray<string>).includes(code);
}

/**
 * 封面上傳失敗 → 使用者看得懂的一句話。
 *
 * 429 單獨處理:BE 限流是 **10 次/分鐘**,而它走的是 slowapi 的預設回應,不見得
 * 帶得出 `error.code` → 用 status 兜底(對齊 R215 對 ads 429 的同型處置)。
 */
export function mapCoverErrorMessage(err: unknown, t: CommonTranslate): string {
  if (!(err instanceof ApiHttpError)) {
    return t('cover.errors.generic');
  }
  if (err.status === 429) {
    return t('cover.errors.rateLimited');
  }
  const code = err.error?.code ?? '';
  if (isKnownCoverErrorClass(code)) {
    return t(`cover.errors.${code}` as const);
  }
  return t('cover.errors.generic');
}
